import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CalendarDays, ChevronLeft, ChevronRight, Plus, UserPlus, ClipboardCheck, Award } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { calendarAPI, leadsAPI, crmFollowupsAPI, aepAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { canCreateEvents, isKRRole, isAgent, getErrorMessage } from '@/lib/utils';
import EventDetailDrawer from '@/components/calendar/EventDetailDrawer';
import EventFormDrawer from '@/components/calendar/EventFormDrawer';
import EventTypeWizard from '@/components/calendar/EventTypeWizard';
import EventPreviewModal from '@/components/calendar/EventPreviewModal';
import { FollowupDetailDrawer } from '@/components/crm/FollowupDetailDrawer';
import { wizardTypeByKey, seedFromWizard } from '@/components/calendar/eventFormHelpers';
import MonthView from '@/components/calendar/MonthView';
import WeekView from '@/components/calendar/WeekView';
import DayView from '@/components/calendar/DayView';
import ListView from '@/components/calendar/ListView';
import {
  buildCalendarItems, computeAepDate,
  startOfWeek, startOfMonth, endOfMonth, startOfDay, endOfDay, addDays,
} from '@/components/calendar/calendarItems';
import { GoogleCalendarConnectPrompt, ConnectGCalChip } from '@/components/integrations/GoogleCalendarConnectPrompt';

const VIEWS = [
  { key: 'month', label: 'Month' },
  { key: 'week', label: 'Week' },
  { key: 'day', label: 'Day' },
  { key: 'list', label: 'List' },
];
const VIEW_KEYS = VIEWS.map((v) => v.key);

// Legacy deep-link values map onto the new taxonomy.
const LEGACY_VIEW = { agenda: 'list', upcoming: 'list' };

function computeRange(view, cursor) {
  if (view === 'month') {
    const gridStart = startOfWeek(startOfMonth(cursor));
    return [gridStart, endOfDay(addDays(gridStart, 41))];
  }
  if (view === 'week') {
    const s = startOfWeek(cursor);
    return [s, endOfDay(addDays(s, 6))];
  }
  if (view === 'day') return [startOfDay(cursor), endOfDay(cursor)];
  // list — rolling 60-day window
  return [startOfDay(cursor), endOfDay(addDays(cursor, 60))];
}

function shiftCursor(view, cursor, direction) {
  const x = new Date(cursor);
  if (view === 'month') x.setMonth(x.getMonth() + direction);
  else if (view === 'week') x.setDate(x.getDate() + 7 * direction);
  else if (view === 'day') x.setDate(x.getDate() + direction);
  else x.setDate(x.getDate() + 30 * direction);
  return x;
}

export default function CalendarPage() {
  const { user } = useAuth();
  const isKR = isKRRole(user);
  const canCreate = canCreateEvents(user);
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { eventId: pathEventId } = useParams();

  // Initial view: ?view= deep-link → persisted → responsive default
  // (mobile intentionally opens on the List/Agenda surface).
  const initialView = useMemo(() => {
    const raw = sp.get('view') || (typeof window !== 'undefined' && localStorage.getItem('kr_calendar_view')) || '';
    const mapped = LEGACY_VIEW[raw] || raw;
    if (VIEW_KEYS.includes(mapped)) return mapped;
    return 'month';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [view, setViewRaw] = useState(initialView);
  const setView = useCallback((v) => {
    setViewRaw(v);
    try { localStorage.setItem('kr_calendar_view', v); } catch {}
    const next = new URLSearchParams(sp);
    next.set('view', v);
    setSp(next, { replace: true });
  }, [sp, setSp]);

  const [cursor, setCursor] = useState(new Date());
  const [status, setStatus] = useState('all');
  const [category, setCategory] = useState('all');
  const [visibility, setVisibility] = useState('all');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [selectedId, setSelectedId] = useState(pathEventId && !isKR ? pathEventId : null);
  const [previewId, setPreviewId] = useState(null);
  const [crmTaskId, setCrmTaskId] = useState(null);
  const [formState, setFormState] = useState({ open: false, mode: null, event: null, typeConfig: null, prefill: null });
  const [wizardOpen, setWizardOpen] = useState(false);

  // Source toggles — overlay lead follow-ups, CRM tasks, AEP prompt.
  const [showEvents, setShowEvents] = useState(true);
  const [showFollowUps, setShowFollowUps] = useState(true);
  const [showCrmTasks, setShowCrmTasks] = useState(true);
  const [showAep, setShowAep] = useState(true);
  const [leadFollowUps, setLeadFollowUps] = useState([]);
  const [crmTasks, setCrmTasks] = useState([]);
  const [aepSummary, setAepSummary] = useState(null);
  const agentUser = isAgent(user);

  // KR admins open the unified Workspace; agents keep the detail drawer.
  const openEventSurface = (id) => {
    if (isKR) navigate(`/events/${id}/workspace`);
    else setSelectedId(id);
  };

  useEffect(() => {
    if (!pathEventId) return;
    if (isKR) { navigate(`/events/${pathEventId}/workspace`, { replace: true }); return; }
    setSelectedId(pathEventId);
  }, [pathEventId, isKR, navigate]);

  useEffect(() => {
    if (sp.get('create') === '1' && !formState.open && !wizardOpen) setWizardOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);

  const [rangeStart, rangeEnd] = useMemo(() => computeRange(view, cursor), [view, cursor]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        start_from: rangeStart.toISOString(),
        start_until: rangeEnd.toISOString(),
        include_archived: includeArchived,
        page_size: 100,
      };
      if (status !== 'all') params.status = status;
      if (category !== 'all') params.category = category;
      if (visibility !== 'all') params.visibility = visibility;
      const requests = [
        calendarAPI.listEvents(params).catch(() => ({ data: { items: [] } })),
        leadsAPI.upcomingFollowUps(200).catch(() => ({ data: { follow_ups: [] } })),
        crmFollowupsAPI.list({ assigned_to_me: !isKR, status: 'open', page_size: 200 }).catch(() => ({ data: { items: [] } })),
        agentUser
          ? aepAPI.myCertifications().catch(() => ({ data: { carriers: [] } }))
          : Promise.resolve({ data: { carriers: [] } }),
      ];
      const [evRes, fuRes, crmRes, aepRes] = await Promise.all(requests);
      setEvents(evRes.data.items || []);
      setLeadFollowUps(fuRes.data.follow_ups || []);
      setCrmTasks(crmRes.data.items || []);
      const carriers = aepRes.data.carriers || [];
      const pending = carriers.filter((c) => c.status !== 'certified' && c.status !== 'not_applicable');
      setAepSummary({ pending: pending.length, total: carriers.length });
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load calendar'));
    } finally {
      setLoading(false);
    }
  }, [rangeStart, rangeEnd, status, category, visibility, includeArchived, isKR, agentUser]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    calendarAPI.categories().then((r) => setCategories(r.data || [])).catch(() => {});
  }, []);

  const rangeLabel = useMemo(() => {
    if (view === 'month') return cursor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    if (view === 'week') {
      const s = startOfWeek(cursor); const e = addDays(s, 6);
      return `${s.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${e.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
    }
    if (view === 'day') return cursor.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
    return `Upcoming from ${cursor.toLocaleDateString()}`;
  }, [view, cursor]);

  // Normalize the four sources once, clipped to the visible range. The
  // toggle chips then filter this set, and the chip counts are taken
  // from it so they always reflect what is actually in the window.
  const rangeItemsAll = useMemo(() => {
    const aep = agentUser && aepSummary?.pending > 0
      ? { pending: aepSummary.pending, total: aepSummary.total, dueDateIso: computeAepDate() }
      : null;
    const all = buildCalendarItems({ events, followUps: leadFollowUps, crmTasks, aep });
    const s = rangeStart.getTime();
    const e = rangeEnd.getTime();
    return all.filter((it) => { const t = it.start.getTime(); return t >= s && t <= e; });
  }, [events, leadFollowUps, crmTasks, aepSummary, agentUser, rangeStart, rangeEnd]);

  const items = useMemo(() => rangeItemsAll.filter((it) => {
    if (it.kind === 'event') return showEvents;
    if (it.kind === 'follow_up') return showFollowUps;
    if (it.kind === 'crm_task') return showCrmTasks;
    if (it.kind === 'aep') return showAep;
    return true;
  }), [rangeItemsAll, showEvents, showFollowUps, showCrmTasks, showAep]);

  const counts = useMemo(() => ({
    event: rangeItemsAll.filter((i) => i.kind === 'event').length,
    follow_up: rangeItemsAll.filter((i) => i.kind === 'follow_up').length,
    crm_task: rangeItemsAll.filter((i) => i.kind === 'crm_task').length,
    aep: rangeItemsAll.filter((i) => i.kind === 'aep').length,
  }), [rangeItemsAll]);

  const openCreate = () => setWizardOpen(true);
  const openEdit = (record) => setFormState({ open: true, mode: 'edit', event: record, typeConfig: null, prefill: null });
  const closeForm = () => setFormState({ open: false, mode: null, event: null, typeConfig: null, prefill: null });
  const handleWizardContinue = (typeKey, basics) => {
    const typeObj = wizardTypeByKey(typeKey);
    const prefill = seedFromWizard(typeObj, basics);
    setWizardOpen(false);
    setFormState({ open: true, mode: 'create', event: null, typeConfig: typeObj, prefill });
  };

  const handleSaved = async (saved) => {
    await load();
    if (selectedId && saved?.event_id === selectedId) {
      setSelectedId(null);
      setTimeout(() => setSelectedId(saved.event_id), 0);
    }
  };
  const handlePublished = async (saved) => {
    await load();
    toast.success(`Published: ${saved?.title || 'event'}`);
    if (saved?.event_id) setSelectedId(saved.event_id);
  };

  // Clicking a calendar item: events open the preview first; other
  // sources navigate straight to their record (unchanged behaviour).
  const handleSelect = (item) => {
    if (item.kind === 'event') { setPreviewId(item.id); return; }
    if (item.kind === 'follow_up') {
      if (item.raw?.lead_exists === false) {
        toast.info('The related lead record is no longer available.');
        return;
      }
      navigate(`/leads/${item.raw.lead_id}`);
    } else if (item.kind === 'crm_task') {
      setCrmTaskId(item.raw.followup_id);
    } else if (item.kind === 'aep') {
      navigate('/aep');
    }
  };

  return (
    <div data-testid="calendar-page">
      <GoogleCalendarConnectPrompt context="events" />
      <PageHeader
        title="Calendar"
        description="Events, trainings, meetings, and community activities you have visibility on."
        breadcrumbs={[{ label: 'Calendar', href: '/calendar' }]}
        actions={
          <div className="flex items-center gap-2">
            <ConnectGCalChip />
            {canCreate ? (
              <Button onClick={openCreate} data-testid="calendar-new-event-btn">
                <Plus size={14} className="mr-1" /> New Event
              </Button>
            ) : null}
          </div>
        }
      />
      <div className="page-content space-y-4">
        {/* Toolbar: view switch + navigation + label */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex overflow-hidden rounded-md border border-slate-200">
            {VIEWS.map((v) => (
              <button
                key={v.key}
                onClick={() => setView(v.key)}
                className={`px-3 py-1.5 text-sm transition ${view === v.key ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                data-testid={`calendar-view-${v.key}`}
              >
                {v.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={() => setCursor(shiftCursor(view, cursor, -1))} data-testid="calendar-prev">
              <ChevronLeft size={14} />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCursor(new Date())} data-testid="calendar-today">
              Today
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCursor(shiftCursor(view, cursor, 1))} data-testid="calendar-next">
              <ChevronRight size={14} />
            </Button>
          </div>
          <span className="text-sm font-medium text-slate-700" data-testid="calendar-range-label">{rangeLabel}</span>

          <div className="ml-auto flex flex-wrap items-center gap-2">
            <div className="inline-flex overflow-hidden rounded-md border border-slate-300 bg-white" data-testid="calendar-visibility-filter">
              {[{ v: 'all', label: 'All' }, { v: 'internal', label: 'Internal' }, { v: 'public', label: 'Public' }].map(({ v, label }) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setVisibility(v)}
                  className={`h-8 px-3 text-xs font-medium transition ${visibility === v ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                  data-testid={`calendar-visibility-${v}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-8 w-40 text-xs" data-testid="calendar-status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {isKR && <SelectItem value="draft">Draft</SelectItem>}
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-8 w-44 text-xs" data-testid="calendar-category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.category} value={c.category}>{c.display_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isKR && (
              <label className="flex items-center gap-1 text-xs text-slate-600">
                <Checkbox checked={includeArchived} onCheckedChange={setIncludeArchived} data-testid="calendar-include-archived" />
                Include archived
              </label>
            )}
          </div>
        </div>

        {/* Source-type filter chips */}
        <div className="flex flex-wrap items-center gap-2 pt-1" data-testid="calendar-source-filter">
          <span className="mr-1 text-xs uppercase tracking-wider text-slate-500">Show:</span>
          <SourceChip active={showEvents} onClick={() => setShowEvents((v) => !v)} tone="sky" testId="filter-events">
            <CalendarDays size={12} /> Events ({counts.event})
          </SourceChip>
          <SourceChip active={showFollowUps} onClick={() => setShowFollowUps((v) => !v)} tone="violet" testId="filter-follow-ups">
            <UserPlus size={12} /> Lead Follow-ups ({counts.follow_up})
          </SourceChip>
          <SourceChip active={showCrmTasks} onClick={() => setShowCrmTasks((v) => !v)} tone="amber" testId="filter-crm-tasks">
            <ClipboardCheck size={12} /> CRM Tasks ({counts.crm_task})
          </SourceChip>
          {agentUser && aepSummary && aepSummary.pending > 0 && (
            <SourceChip active={showAep} onClick={() => setShowAep((v) => !v)} tone="emerald" testId="filter-aep">
              <Award size={12} /> AEP ({aepSummary.pending} pending)
            </SourceChip>
          )}
        </div>

        {loading ? (
          <p className="py-8 text-center text-sm text-slate-500" data-testid="calendar-loading">Loading calendar…</p>
        ) : items.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center" data-testid="calendar-empty">
              <CalendarDays size={28} className="mx-auto mb-2 text-slate-400" />
              <p className="text-sm text-slate-600">Nothing on your calendar in this window.</p>
              <p className="text-xs text-slate-500">Try widening the range, clearing filters, or scheduling a follow-up.</p>
            </CardContent>
          </Card>
        ) : view === 'month' ? (
          <MonthView items={items} cursor={cursor} onSelect={handleSelect} />
        ) : view === 'week' ? (
          <WeekView items={items} cursor={cursor} onSelect={handleSelect} />
        ) : view === 'day' ? (
          <DayView items={items} cursor={cursor} onSelect={handleSelect} />
        ) : (
          <ListView items={items} onSelect={handleSelect} />
        )}
      </div>

      <EventPreviewModal
        eventId={previewId}
        open={!!previewId}
        onClose={() => setPreviewId(null)}
        isKR={isKR}
        onViewEvent={(id) => { setPreviewId(null); openEventSurface(id); }}
        onEdit={(record) => { setPreviewId(null); openEdit(record); }}
      />

      <EventDetailDrawer
        eventId={selectedId}
        onClose={() => setSelectedId(null)}
        onChanged={load}
        onEdit={(record) => { setSelectedId(null); openEdit(record); }}
      />

      <EventFormDrawer
        open={formState.open}
        mode={formState.mode}
        event={formState.event}
        typeConfig={formState.typeConfig}
        prefill={formState.prefill}
        categories={categories}
        onClose={closeForm}
        onSaved={handleSaved}
        onPublished={handlePublished}
        onCoverChanged={load}
      />

      <EventTypeWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onContinue={handleWizardContinue}
      />

      <FollowupDetailDrawer
        open={!!crmTaskId}
        onOpenChange={(o) => !o && setCrmTaskId(null)}
        followupId={crmTaskId}
        onChanged={load}
      />
    </div>
  );
}

const TONE_MAP = {
  sky:     { bg: 'bg-sky-50',     border: 'border-sky-200',     text: 'text-sky-700' },
  violet:  { bg: 'bg-violet-50',  border: 'border-violet-200',  text: 'text-violet-700' },
  amber:   { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-700' },
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700' },
};

function SourceChip({ active, onClick, tone = 'sky', children, testId }) {
  const t = TONE_MAP[tone] || TONE_MAP.sky;
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition ${
        active ? `${t.bg} ${t.border} ${t.text}` : 'border-slate-200 bg-white text-slate-400 hover:text-slate-600'
      }`}
    >
      {children}
    </button>
  );
}
